import React, { useState } from 'react';
import FieldWrapper from '../../../../../common/FieldWrapper';
import Select from 'react-select';
import { Coupon } from '../../../../../types/generated.types';
import { HiChevronDown } from 'react-icons/hi2';
import { Button, Field } from '../../../../../common/Form';
import AutoResizeTextarea from '../../../../../common/AutoResizeTextarea';
import Modal from '../../../../../common/Modal';

import ReactDOM from 'react-dom';

/**
 * Checks if any of the "show more" fields have values
 * @param coupon - The coupon to check
 * @returns true if any field has a value, false otherwise
 */
const hasShowMoreValues = (coupon: Partial<Coupon>): boolean => {
  return !!(
    (coupon.code && coupon.code.trim() !== '') ||
    (coupon.productLink && coupon.productLink.trim() !== '') ||
    (coupon.imageURL && coupon.imageURL.trim() !== '')
  );
};

const detectCodeType = (value: string): string | null => {
  if (!value) return null;

  const numeric = /^[0-9]+$/.test(value);

  if (numeric) {
    if (value.length === 8) return 'EAN8';
    if (value.length === 13) return 'EAN13';
    if (value.length === 12) return 'UPCA';
    if (value.length === 5) return 'EAN5';
    if (value.length === 2) return 'EAN2';
  }

  return null;
};

const codeTypeOptions = [
  { value: 'TEXT', label: 'TEXT' },
  { value: 'QR', label: 'QR' },
  { value: 'CODE39', label: 'CODE39' },
  { value: 'EAN13', label: 'EAN13' },
  { value: 'EAN8', label: 'EAN8' },
  { value: 'EAN5', label: 'EAN5' },
  { value: 'EAN2', label: 'EAN2' },
  { value: 'UPCA', label: 'UPCA' },
  { value: 'UPCE', label: 'UPCE' },
  { value: 'CODE128', label: 'CODE128' },
  { value: 'CODE128A', label: 'CODE128A' },
  { value: 'CODE128B', label: 'CODE128B' },
  { value: 'CODE128C', label: 'CODE128C' },
  { value: 'ITF', label: 'ITF' },
  { value: 'ITF14', label: 'ITF14' },
  { value: 'MSI10', label: 'MSI10' },
  { value: 'MSI11', label: 'MSI11' },
  { value: 'MSI1010', label: 'MSI1010' },
  { value: 'MSI1110', label: 'MSI1110' },
  { value: 'pharmacode', label: 'pharmacode' },
  { value: 'codabar', label: 'codabar' },
  { value: 'NONE', label: 'NONE' },
];

const codePlaceholders: Record<string, string> = {
  TEXT: 'Valfri text',
  QR: 'Länk eller valfri text',
  CODE39: 'ABC-123',
  CODE128: 'ABC123456',
  CODE128A: 'ABC123',
  CODE128B: 'Abc123',
  CODE128C: '12345678',
  EAN13: '1234567890123',
  EAN8: '12345678',
  EAN5: '12345',
  EAN2: '12',
  UPCA: '123456789012',
  UPCE: '1234567',
  ITF: '123456',
  ITF14: '12345678901234',
  MSI10: '1234567890',
  MSI11: '12345678901',
  MSI1010: '1234567890',
  MSI1110: '12345678901',
  pharmacode: '12345',
  codabar: 'A12345B',
  NONE: '',
};

interface CouponDetailsEditProps {
  coupon: Partial<Coupon>;
  onChange: (updates: Partial<Coupon>) => void;
  onBlur: () => void;
}

const CouponDetailsEdit: React.FC<CouponDetailsEditProps> = ({ coupon, onChange, onBlur }) => {
  // Initialize showMore: expand IF fields have values, otherwise collapsed
  const [showMore, setShowMore] = useState(() => hasShowMoreValues(coupon));

  const handleChange = (field: string, value: string | number | null) => {
    onChange({ [field]: value });
  };

  // React-select styling
  const selectPortalTarget = typeof document !== 'undefined' ? document.body : null;
  const selectPortalStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };
  const codeTypeMenuStyles = {
    ...selectPortalStyles,
    // Only the dropdown menu should be wider (not the control)
    menu: (base: any) => ({ ...base, minWidth: 220 }),
  };

  const selectedCodeType = codeTypeOptions.find(opt => opt.value === coupon.codeType);

  const currentPlaceholder =
    coupon.codeType && codePlaceholders[coupon.codeType]
      ? codePlaceholders[coupon.codeType]
      : '0000000000000';

  const handleCodeChange = (value: string) => {
    handleChange('code', value);

    const detected = detectCodeType(value);

    if (detected && detected !== coupon.codeType) {
      handleChange('codeType', detected);
    }
  };

  return (
    <div className="coupon-details coupon-details-edit flex flex-col">
      <h4>Kupong</h4>
      <FieldWrapper name="discount" title="Rabatt">
        <input
          type="text"
          value={coupon.discount || ''}
          onChange={(e) => handleChange('discount', e.target.value)}
          onBlur={onBlur}
          title="Rabatt"
          placeholder='ex. "2 för 1", "50%"...'
          required
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </FieldWrapper>

      <FieldWrapper name="smallTitle" title="Liten titel">
        <input
          type="text"
          value={coupon.smallTitle || ''}
          onChange={(e) => handleChange('smallTitle', e.target.value)}
          onBlur={onBlur}
          title="Liten titel"
          placeholder='ex. "På valfri huvudrätt"...'
          required
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </FieldWrapper>

      <FieldWrapper name="description" title="Beskrivning">
        <AutoResizeTextarea
          value={coupon.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          onBlur={onBlur}
          rows={1}
          placeholder='ex. "Gäller alla vardagar"...'
          title="Beskrivning"
          onPointerDown={(e) => e.stopPropagation()}
        />
      </FieldWrapper>

      <FieldWrapper name="terms" title="Villkor">
        <AutoResizeTextarea
          value={coupon.terms || ''}
          onChange={(e) => handleChange('terms', e.target.value)}
          onBlur={onBlur}
          rows={1}
          placeholder='ex. "Gäller ej alkohol"...'
          title="Villkor"
          onPointerDown={(e) => e.stopPropagation()}
        />
      </FieldWrapper>

      <FieldWrapper name="estimatedSavings" title="Förväntad besparing (kr)">
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={coupon.estimatedSavings !== undefined && coupon.estimatedSavings !== null ? coupon.estimatedSavings : ''}
            onChange={(e) => handleChange('estimatedSavings', e.target.value ? parseFloat(e.target.value) : null)}
            onBlur={onBlur}
            placeholder=''
            title="Förväntad besparing (kr)"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </div>
      </FieldWrapper>

      {/* "Visa mer" section - always visible */}
      <button
        type="button"
        className="show-more flex items-center gap-1 mt"
        onClick={() => setShowMore(!showMore)}
      >
        {showMore ? 'Visa mindre' : 'Visa mer'}
        <HiChevronDown size={14} className={showMore ? 'rotated' : ''} />
      </button>


      {showMore && (
        <div className="flex flex-col show-more-content">
          <div className="flex gap-1 mt">
            <FieldWrapper name="code" title="Kod">
              <input
                type="text"
                value={coupon.code || ''}
                onChange={(e) => handleCodeChange(e.target.value)}
                onBlur={onBlur}
                placeholder={currentPlaceholder}
                title="Kod"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                onPointerDown={(e) => e.stopPropagation()}
              />
            </FieldWrapper>

            <FieldWrapper name="codeType" title="Typ">
              <Select
                className="codeType"
                classNamePrefix="react-select"
                options={codeTypeOptions}
                value={selectedCodeType || null}
                onChange={(option) => handleChange('codeType', option?.value || null)}
                onBlur={onBlur}
                placeholder="Typ"
                title="Typ"
                menuPortalTarget={selectPortalTarget as any}
                styles={codeTypeMenuStyles as any}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                onPointerDown={(e) => e.stopPropagation()}
              />
            </FieldWrapper>
          </div>

          <FieldWrapper name="productLink" title="Produkt-URL (länk)">
            <input
              type="text"
              value={coupon.productLink || ''}
              onChange={(e) => handleChange('productLink', e.target.value)}
              onBlur={onBlur}
              placeholder='https://example.org'
              title="Produkt-URL"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              onPointerDown={(e) => e.stopPropagation()}
            />
          </FieldWrapper>

          <Field
            type="image"
            name="imageURL"
            title="Bild (max 1MB)"
            value={coupon.imageURL}
            onChange={(e) => {
              handleChange('imageURL', e['imageURL']);
              onBlur();
            }}
          />
        </div>
      )}
    </div>
  );
}

export default CouponDetailsEdit;

